/**
 * Central registry of Irish mortgage lenders for the multi-bank prototype.
 * Each bank maps to its branding (accent color, logo path) and form overrides
 * (disclaimer text substitutions, field label variations).
 *
 * Bank names must match the `bank.name` values stored in the backend exactly.
 */

export const BANK_CONFIG = {
  "Bank of Ireland": {
    accentColor: "#0000FF",          // verified: BOI rebrand to pure blue
    accentColorLight: "#E6E6FF",
    shortName: "BOI",
    logoPath: "/image/banks/boi.svg",
    legalEntityName: "Bank of Ireland",
    mortgageRefLabel: "Loan Account Number",
  },
  "AIB": {
    accentColor: "#632874",          // verified: official AIB purple
    accentColorLight: "#F0E8F3",
    shortName: "AIB",
    logoPath: "/image/banks/aib.png",
    legalEntityName: "AIB",
    mortgageRefLabel: "AIB Reference Number",
  },
  "Permanent TSB": {
    accentColor: "#FC4C02",          // verified: official PTSB vivid orange
    accentColorLight: "#FFF0E6",
    shortName: "PTSB",
    logoPath: "/image/banks/ptsb.svg",
    legalEntityName: "Permanent TSB",
    mortgageRefLabel: "PTSB Reference Number",
  },
  "EBS": {
    accentColor: "#E21D22",          // verified: EBS primary red (logo confirms)
    accentColorLight: "#FDE8E8",
    shortName: "EBS",
    logoPath: "/image/banks/ebs.png",
    legalEntityName: "EBS",
    mortgageRefLabel: "EBS Mortgage Reference",
  },
  "Haven Mortgages": {
    accentColor: "#78BE20",          // verified: Haven neon/lime green (logo confirms)
    accentColorLight: "#F2FAE8",
    shortName: "Haven",
    logoPath: "/image/banks/haven.png",
    legalEntityName: "Haven Mortgages",
    mortgageRefLabel: "Haven Reference Number",
  },
  "Avant Money": {
    accentColor: "#00A651",          // plausible: Avant Money green
    accentColorLight: "#E6F9EF",
    shortName: "Avant",
    logoPath: "/image/banks/avant.png",
    legalEntityName: "Avant Money",
    mortgageRefLabel: "Avant Money Reference",
  },
  "Finance Ireland": {
    accentColor: "#008770",          // verified: Finance Ireland primary teal from CSS
    accentColorLight: "#E6F5F0",
    shortName: "FI",
    logoPath: null,
    legalEntityName: "Finance Ireland",
    mortgageRefLabel: "Mortgage Reference Number",
  },
  "Probate Finance Ireland Ltd": {
    // Sampled from the PFI logo Brian Winters sent on 4 Nov 2025 ("Fwd: PFI Logo"):
    // bridge #176872 (teal green), wordmark #123958 (navy). The Pepper crimson used
    // before was for a servicer demo; PFI Ltd is the trading entity on the report.
    accentColor: "#176872",
    accentColorLight: "#E8F0F1",
    shortName: "PFI",
    logoPath: "/image/banks/pfi.png",  // colour mark for white cards; the PDF header uses the white cut
    legalEntityName: "Probate Finance Ireland Ltd", // lender of record; Pepper is the servicer
    mortgageRefLabel: "PFI Loan Reference",
    // Report forms this bank can instruct. The first entry is the default; the id
    // is stored on the valuation (valuation.formType) and picks the template in
    // bank-service.js. Banks without formTypes have one implicit form.
    formTypes: [
      { id: "probate-bridging", label: "Probate bridging valuation", description: "Probate Finance Ireland's own report: use, eligibility, occupancy, agricultural land and holiday-home branches." },
      { id: "residential", label: "Residential valuation", description: "The standard residential mortgage valuation report." },
    ],
  },
  "ICS Mortgages": {
    accentColor: "#1A1A6C",          // plausible: ICS dark navy/purple
    accentColorLight: "#EBEBF5",
    shortName: "ICS",
    logoPath: "/image/banks/ics.png",
    legalEntityName: "ICS Mortgages",
    mortgageRefLabel: "ICS Reference Number",
  },
  "MoCo": {
    accentColor: "#2ED3AE",          // verified: MoCo primary teal from CSS
    accentColorLight: "#E9FAF5",
    shortName: "MoCo",
    logoPath: "/image/banks/moco.png",
    legalEntityName: "MoCo",
    mortgageRefLabel: "MoCo Reference Number",
  },
  "Nua Money": {
    accentColor: "#18DD67",          // verified: Nua accent green from CSS
    accentColorLight: "#E8FBF0",
    shortName: "Nua",
    logoPath: "/image/banks/nua.svg",
    legalEntityName: "Nua Money",
    mortgageRefLabel: "Nua Reference Number",
  },
  "Ulster Bank": {
    accentColor: "#02296A",          // verified: Ulster Bank official dark blue
    accentColorLight: "#E6EBF3",
    shortName: "UB",
    logoPath: "/image/banks/ulster.svg",
    legalEntityName: "Ulster Bank",
    mortgageRefLabel: "Ulster Bank Reference",
  },
  "Irish Credit Union": {
    accentColor: "#026991",          // sampled from logo background — dark teal-blue
    accentColorLight: "#E6F0F4",
    shortName: "ICU",
    logoPath: "/image/banks/icu.png",
    legalEntityName: "Irish Credit Union",
    mortgageRefLabel: "Credit Union Reference",
  },
};

export const getBankConfig = (bankName) =>
  BANK_CONFIG[bankName] || {
    accentColor: "#6B7280",
    accentColorLight: "#F3F4F6",
    shortName: "?",
    logoPath: null,
    legalEntityName: bankName || "the Lender",
    mortgageRefLabel: "Mortgage Reference Number",
  };

/** The report forms a bank can choose between when instructing; [] when it has one implicit form. */
export const getFormTypes = (bankName) => getBankConfig(bankName).formTypes || [];

/** The form used when a valuation has no formType of its own (the first configured one). */
export const getDefaultFormType = (bankName) => getFormTypes(bankName)[0]?.id ?? null;

/** Resolves a valuation's formType against the bank's options: unknown or missing → the default. */
export const resolveFormType = (bankName, formType) => {
  const types = getFormTypes(bankName);
  if (types.length === 0) return null;
  return types.some((t) => t.id === formType) ? formType : types[0].id;
};

export const getFormTypeLabel = (bankName, formType) =>
  getFormTypes(bankName).find((t) => t.id === resolveFormType(bankName, formType))?.label ?? null;
